import React, { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { Folder as FolderIcon, InsertDriveFile, MoreVert as MoreVertIcon } from "@mui/icons-material";
import { Document, FolderWithDocuments } from "../../types/folderTypes";
import SuperAdminUserService from "../../service/superadmin/userService";
import FileViewerModal from "./FileViewerModal";

interface FolderDocumentTableProps {
    folders: FolderWithDocuments[];
    unassignedDocuments: Document[];
    folderAnchorEl: HTMLElement | null;
    fileAnchorEl: HTMLElement | null;
    selectedFolder: FolderWithDocuments | null;
    selectedFile: Document | null;
    userRole: string;
    isCompanyAdmin: number;
    handleFolderClick: (folderId: string) => void;
    handleFolderMenuOpen: (event: React.MouseEvent<HTMLButtonElement>, folder: FolderWithDocuments) => void;
    handleFolderMenuClose: () => void;
    handleEditFolder?: (folder: FolderWithDocuments) => void;
    handleDownloadFolder: (folder: FolderWithDocuments) => void;
    handleDeleteFolder?: (folderId: number) => void;
    handleDocumentClick?: (documentPath: string) => void;
    handleFileMenuOpen: (event: React.MouseEvent<HTMLButtonElement>, doc: Document) => void;
    handleFileMenuClose: () => void;
    handleDownloadFile: (docId: string, docName: string) => void;
    confirmDeleteFile?: (docId: string) => void;
    handleEditDocument?: (doc: Document) => void;
}

const FolderDocumentTable: React.FC<FolderDocumentTableProps> = ({
    folders,
    unassignedDocuments,
    folderAnchorEl,
    fileAnchorEl,
    selectedFolder,
    selectedFile,
    userRole,
    isCompanyAdmin,
    handleFolderClick,
    handleFolderMenuOpen,
    handleFolderMenuClose,
    handleEditFolder,
    handleDownloadFolder,
    handleDeleteFolder,
    handleDocumentClick,
    handleFileMenuOpen,
    handleFileMenuClose,
    handleDownloadFile,
    confirmDeleteFile,
    handleEditDocument,
}) => {
    const [userNames, setUserNames] = useState<any>({});
    const [viewerDoc, setViewerDoc] = useState<Document | null>(null);

    useEffect(() => {
        const userIds = new Set<any>();

        const isValidUserId = (id: any): boolean => {
            return !!id && id !== "0" && id !== "Unknown" && id !== "null" && id !== "undefined";
        };

        // Collect unique user IDs from folders
        folders.forEach(folder => {
            const single = folder.activity_log?.user_id;
            const first = folder.activity_logs?.[0]?.user_id;

            if (isValidUserId(single)) userIds.add(single);
            if (isValidUserId(first)) userIds.add(first);
        });

        // Collect unique user IDs from documents
        unassignedDocuments.forEach(doc => {
            const first = doc.activity_logs?.[0]?.user_id;
            const single = doc.activity_log?.user_id;
            // const updatedBy = doc.updated_by;

            if (isValidUserId(first)) userIds.add(first);
            if (isValidUserId(single)) userIds.add(single);
            // if (isValidUserId(updatedBy)) userIds.add(updatedBy);
        });

        // console.log(userIds, 'User Ids');

        const fetchUserNames = async () => {
            const namesMap: { [key: string]: string } = {};

            await Promise.all(Array.from(userIds).map(async (userId) => {
                try {
                    const user = await SuperAdminUserService.getUserById(Number(userId));
                    namesMap[userId] = user.name;
                } catch {
                    namesMap[userId] = "Unknown";
                }
            }));

            setUserNames(namesMap);
        };

        if (userIds.size > 0) fetchUserNames();
    }, []);



    return (
        <TableContainer component="div" className="bg-white">
            <Table>
                <TableHead className="bg-gray-100">
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Files</TableCell>
                        <TableCell>Last Updated</TableCell>
                        {(userRole === 'superadmin' || userRole === 'admin' || (userRole === 'user' && isCompanyAdmin === 1)) && (
                            <TableCell>Actions</TableCell>
                        )}


                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* Render Folders */}
                    {folders.map((folder) => (
                        <TableRow key={folder.id} className="hover:bg-gray-50 cursor-pointer">
                            <TableCell onClick={() => handleFolderClick(folder.id.toString())} className="flex items-center">
                                <FolderIcon className="text-[#632DE9] text-4xl mr-2" />
                                {folder.folder_name}
                            </TableCell>
                            <TableCell>{folder.total_documents > 0 ? `${folder.total_documents} files` : "-"}</TableCell>
                            <TableCell>
                                {folder.activity_log && (
                                    <>
                                        <Typography variant="body2" className="text-sm font-medium">
                                            {folder.activity_log.action_type} by {userNames[folder.activity_log.user_id] || 'Unknown'}
                                        </Typography>
                                        <Typography variant="caption" className="text-xs text-gray-500">
                                            {new Date(folder.activity_log.logged_at).toLocaleString("en-GB", {
                                                year: "numeric",
                                                month: "2-digit",
                                                day: "2-digit",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                second: "2-digit",
                                                hour12: false,
                                            })}
                                        </Typography>
                                    </>
                                )}

                                {folder.activity_logs?.[0] ? (
                                    <>
                                        <Typography variant="body2" className="text-sm font-medium">
                                            {folder.activity_logs[0].action_type} by {userNames[folder.activity_logs[0].user_id] || 'Unknown'}
                                        </Typography>
                                        <Typography variant="caption" className="text-xs text-gray-500">
                                            {new Date(folder.activity_logs[0].created_at).toLocaleString("en-GB", {
                                                year: "numeric",
                                                month: "2-digit",
                                                day: "2-digit",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                second: "2-digit",
                                                hour12: false,
                                            })}
                                        </Typography>
                                    </>
                                ) : '-'}


                            </TableCell>
                            {(userRole === 'superadmin' || userRole === 'admin' || (userRole === 'user' && isCompanyAdmin === 1)) && (
                                <TableCell>
                                    <IconButton onClick={(event) => handleFolderMenuOpen(event, folder)} className="text-gray-400">
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Menu
                                        anchorEl={folderAnchorEl}
                                        open={Boolean(folderAnchorEl) && selectedFolder?.id === folder.id}
                                        onClose={handleFolderMenuClose}
                                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                                    >
                                        <>
                                            <MenuItem onClick={(e) => { e.stopPropagation(); handleEditFolder?.(folder); handleFolderMenuClose?.(); }}>
                                                Edit
                                            </MenuItem>
                                            <MenuItem onClick={(e) => { e.stopPropagation(); handleDeleteFolder?.(folder.id); handleFolderMenuClose?.(); }} className="text-red-500">
                                                Delete
                                            </MenuItem>
                                            <MenuItem onClick={(e) => { e.stopPropagation(); handleDownloadFolder?.(folder); handleFolderMenuClose?.(); }}>
                                                Download
                                            </MenuItem>
                                        </>

                                    </Menu>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}

                    <FileViewerModal document={viewerDoc} onClose={() => setViewerDoc(null)} />

                    {/* Render Documents */}
                    {unassignedDocuments.map((doc) => (
                        <TableRow key={doc.id} className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => setViewerDoc(doc)}>
                            <TableCell className="flex items-center">
                                <InsertDriveFile className="text-gray-600 text-4xl mr-2" />
                                {doc.document_name}
                            </TableCell>
                            <TableCell>{(doc.document_size / 1024 / 1024).toFixed(2)} MB</TableCell>
                            <TableCell>
                                {doc.activity_log && (
                                    <>
                                        <Typography variant="body2" className="text-sm font-medium">
                                            {doc.activity_log.action_type} by {userNames[doc.activity_log.user_id] || 'Unknown'}
                                        </Typography>
                                        <Typography variant="caption" className="text-xs text-gray-500">
                                            {new Date(doc.activity_log.logged_at).toLocaleString("en-GB", {
                                                year: "numeric",
                                                month: "2-digit",
                                                day: "2-digit",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                second: "2-digit",
                                                hour12: false,
                                            })}
                                        </Typography>
                                    </>
                                )}

                                {doc.updated_by && !doc.activity_logs && (
                                    <>
                                        <Typography variant="body2" className="text-sm font-medium">
                                            {doc.action_type} by {doc.updated_by}
                                        </Typography>
                                        <Typography variant="caption" className="text-xs text-gray-500">
                                            {new Date(doc.updated_at).toLocaleString("en-GB", {
                                                year: "numeric",
                                                month: "2-digit",
                                                day: "2-digit",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                second: "2-digit",
                                                hour12: false,
                                            })}
                                        </Typography>
                                    </>
                                )}

                                {userRole === 'admin' && doc.activity_logs?.[0] && (
                                    <>
                                        <Typography variant="body2" className="text-sm font-medium">
                                            {doc.activity_logs[0].action_type} by {userNames[doc.activity_logs[0].user_id] || 'Unknown'}
                                        </Typography>
                                        <Typography variant="caption" className="text-xs text-gray-500">
                                            {new Date(doc.activity_logs[0].created_at).toLocaleString("en-GB", {
                                                year: "numeric",
                                                month: "2-digit",
                                                day: "2-digit",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                second: "2-digit",
                                                hour12: false,
                                            })}
                                        </Typography>
                                    </>
                                )}

                            </TableCell>
                            {(userRole === 'superadmin' || userRole === 'admin' || (userRole === 'user' && isCompanyAdmin === 1)) && (
                                <TableCell>
                                    <IconButton onClick={(event) => handleFileMenuOpen(event, doc)} className="text-gray-400">
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Menu
                                        anchorEl={fileAnchorEl}
                                        open={Boolean(fileAnchorEl) && selectedFile?.id === doc.id}
                                        onClose={handleFileMenuClose}
                                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                                    >

                                        <MenuItem onClick={(e) => { e.stopPropagation(); handleEditDocument?.(doc); handleFileMenuClose?.(); }}>
                                            Edit
                                        </MenuItem>
                                        <MenuItem onClick={(e) => { e.stopPropagation(); handleDownloadFile?.(doc.id.toString(), doc.document_name); handleFileMenuClose?.(); }}>
                                            Download
                                        </MenuItem>
                                        <MenuItem onClick={(e) => { e.stopPropagation(); confirmDeleteFile?.(doc.id.toString()); handleFileMenuClose?.(); }} className="text-red-500">
                                            Delete
                                        </MenuItem>
                                    </Menu>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default FolderDocumentTable;
